import { assign } from 'xstate';
import logger from '../../../logger';
import {
  addNoteToAreaOfFocusCollection,
  removeNoteFromAreaOfFocusCollection,
  createAreaOfFocusCollection,
  renameAreaOfFocusCollection,
  deleteAreaOfFocusCollection,
} from '../../cloud/areasOfFocusCollections';
import { writeAreaOfFocus, deleteAreaOfFocusFromLocalDB } from '../../offline';
import { generateAreasOfFocusCollectionsIterable } from '../../../utils/helpers';
import type { AppStateContext } from '../../../types/appState';

// ---------------------------------------------------------------------------
// Service functions
// ---------------------------------------------------------------------------

async function assignNoteService(context: AppStateContext, event: any) {
  const { note, noteId: eventNoteId, collectionId: eventCollectionId, isSelectedAreaOfFocus } = event.data;
  const noteId = eventNoteId || note?.note_id;
  const { areasOfFocusCollections, selectedAreaOfFocus } = context;
  const collectionId = eventCollectionId || selectedAreaOfFocus?.area_of_focus_collection_id;
  const collection = areasOfFocusCollections?.[collectionId] || selectedAreaOfFocus;

  if (!collectionId || !noteId) throw new Error('assignNoteToAreaOfFocus: missing collectionId or noteId');

  return addNoteToAreaOfFocusCollection({ collectionId, noteId, isSelectedAreaOfFocus: !!isSelectedAreaOfFocus, AOFObject: collection as any });
}

async function removeNoteService(context: AppStateContext, event: any) {
  const { noteId, collectionId } = event.data;
  if (!noteId || !collectionId) throw new Error('removingNoteFromAOFCollection: missing noteId or collectionId');

  const collection = context.areasOfFocusCollections?.[collectionId];
  if (!collection) return { collectionId, noteId, updatedCollection: null };

  const updatedCollection = {
    ...collection,
    order_of_items: (collection.order_of_items || []).filter((id: string) => id !== noteId),
    area_of_focus_collection_notes: ((collection as any).area_of_focus_collection_notes || []).filter((id: string) => id !== noteId),
    selected_stack: (collection.selected_stack || []).filter((id: string) => id !== noteId),
  };

  await writeAreaOfFocus({ areaOfFocus: updatedCollection });

  removeNoteFromAreaOfFocusCollection({ collectionId, noteId }).catch((error: any) => {
    logger.error({ error, noteId, collectionId }, 'removingNoteFromAOFCollection: cloud remove failed');
  });

  return { collectionId, noteId, updatedCollection };
}

async function createAOFService(context: AppStateContext, event: any) {
  const { title } = event.data;
  const newCollection = await createAreaOfFocusCollection({ title });
  if (newCollection?.area_of_focus_collection_id) {
    await writeAreaOfFocus({ areaOfFocus: newCollection });
  }
  return { newCollection };
}

async function renameAOFService(context: AppStateContext, event: any) {
  const { collectionId, title } = event.data;
  const existing = context.areasOfFocusCollections![collectionId];
  const apiResult = await renameAreaOfFocusCollection({ collectionId, title });
  const toWrite = (apiResult?.area_of_focus_collection_id)
    ? apiResult
    : { ...existing, title, date_modified: new Date().toISOString() };
  if (toWrite?.area_of_focus_collection_id) {
    await writeAreaOfFocus({ areaOfFocus: toWrite });
  }
  return { collectionId, title, apiResult };
}

async function deleteAOFService(context: AppStateContext, event: any) {
  const { collectionId } = event.data;
  await deleteAreaOfFocusCollection({ collectionId });
  await deleteAreaOfFocusFromLocalDB({ collectionId });
  return { collectionId };
}

async function mergeAOFService(context: AppStateContext, event: any) {
  const { sourceId, destinationId } = event.data;
  const sourceCollection = context.areasOfFocusCollections![sourceId];
  if (!sourceCollection) throw new Error(`Source collection ${sourceId} not found`);

  for (const noteId of sourceCollection.order_of_items || []) {
    await addNoteToAreaOfFocusCollection({ collectionId: destinationId, noteId });
  }

  await deleteAreaOfFocusCollection({ collectionId: sourceId });
  await deleteAreaOfFocusFromLocalDB({ collectionId: sourceId });

  const destinationCollection = context.areasOfFocusCollections![destinationId];
  return { sourceId, destinationId, destinationCollection };
}

// ---------------------------------------------------------------------------
// Idle event routing
// ---------------------------------------------------------------------------

export const aofIdleOn = {
  assignNoteToAreaOfFocus: { target: 'assignNoteToAreaOfFocus' },
  removeNoteFromAOFCollection: { target: 'removingNoteFromAOFCollection' },
  createAreaOfFocusCollection: { target: 'creatingAreaOfFocusCollection' },
  renameAreaOfFocusCollection: { target: 'renamingAreaOfFocusCollection' },
  deleteAreaOfFocusCollection: { target: 'deletingAreaOfFocusCollection' },
  mergeAreaOfFocusCollections: { target: 'mergingAreaOfFocusCollections' },
};

// ---------------------------------------------------------------------------
// Top-level event handlers (never dropped when machine is busy)
// ---------------------------------------------------------------------------

export const aofTopLevelOn = {
  // selectAreasOfFocusCollection: never dropped when machine is busy (e.g. savingNote)
  selectAreasOfFocusCollection: { target: '.selectingAreasOfFocusCollection' },
  // closeAndSelectNextNote + selectNote at top level so tab clicks during async ops aren't dropped
  closeAndSelectNextNote: { target: '.closingNote' },
  selectNote: { target: '.selectingNote' },
};

// ---------------------------------------------------------------------------
// State definitions
// ---------------------------------------------------------------------------

export const aofStates = {
  assignNoteToAreaOfFocus: {
    id: 'assignNoteToAreaOfFocus',
    entry: [assign<AppStateContext, any>((context, event) => {
      logger.info({ context, event }, 'assignNoteToAreaOfFocus data');
      return {};
    })],
    initial: 'assigningNoteToAreaOfFocus',
    states: {
      assigningNoteToAreaOfFocus: {
        entry: [(context: any, event: any) => {
          logger.info({ context, event }, 'assigningNoteToAreaOfFocus data');
        }],
        invoke: {
          src: assignNoteService,
          onDone: {
            target: '#idle',
            actions: assign<AppStateContext, any>((context, event) => {
              const updatedAreaOfFocus = event.data.result;
              if (!updatedAreaOfFocus) return {};

              const updatedAreasOfFocusCollections = {
                ...context.areasOfFocusCollections,
                [updatedAreaOfFocus.area_of_focus_collection_id]: updatedAreaOfFocus,
              };
              const areasOfFocusCollectionsList = Object.values(updatedAreasOfFocusCollections);
              const isSelectedCollection = context.selectedAreaOfFocus?.area_of_focus_collection_id === updatedAreaOfFocus.area_of_focus_collection_id;
              const updatedOrderOfItems = updatedAreaOfFocus.order_of_items || [];
              const updatedOpenNoteObjects = isSelectedCollection
                ? updatedOrderOfItems.reduce((acc: any, id: string) => { acc[id] = context.notes?.[id] || null; return acc; }, {})
                : context.areaOfFocusOpenNoteObjects;

              return {
                ...(isSelectedCollection ? { selectedAreaOfFocus: updatedAreaOfFocus } : {}),
                areasOfFocusCollections: updatedAreasOfFocusCollections,
                areasOfFocusCollectionsIterable: areasOfFocusCollectionsList,
                ...(isSelectedCollection ? { areaOfFocusOpenNoteIds: updatedOrderOfItems, areaOfFocusOpenNoteObjects: updatedOpenNoteObjects } : {}),
              };
            }),
          },
          onError: {
            target: '#idle',
            actions: (context: any, event: any) => { logger.error({ event }, 'assignNoteToAreaOfFocus: failed'); },
          },
        },
      },
    },
  },

  removingNoteFromAOFCollection: {
    id: 'removingNoteFromAOFCollection',
    invoke: {
      src: removeNoteService,
      onDone: {
        target: '#idle',
        actions: assign<AppStateContext, any>((context, event) => {
          const { collectionId, updatedCollection } = event.data;
          if (!updatedCollection) return {};

          const updatedAreasOfFocusCollections = {
            ...context.areasOfFocusCollections,
            [collectionId]: updatedCollection,
          };
          const isSelectedCollection = context.selectedAreaOfFocus?.area_of_focus_collection_id === collectionId;
          const updatedOrderOfItems = updatedCollection.order_of_items || [];
          const updatedOpenNoteObjects = isSelectedCollection
            ? updatedOrderOfItems.reduce((acc: any, id: string) => { acc[id] = context.notes?.[id] || null; return acc; }, {})
            : context.areaOfFocusOpenNoteObjects;
          const isNowEmpty = isSelectedCollection && updatedOrderOfItems.length === 0;

          if (isNowEmpty) {
            logger.warn({ collectionId, collectionTitle: updatedCollection.title }, 'removingNoteFromAOFCollection: last note removed from selected area');
          }

          return {
            areasOfFocusCollections: updatedAreasOfFocusCollections,
            areasOfFocusCollectionsIterable: Object.values(updatedAreasOfFocusCollections),
            ...(isSelectedCollection ? {
              selectedAreaOfFocus: updatedCollection,
              areaOfFocusOpenNoteIds: updatedOrderOfItems,
              areaOfFocusOpenNoteObjects: updatedOpenNoteObjects,
              ...(isNowEmpty ? { selectedNote: null, selectedNoteId: null } : {}),
            } : {}),
          };
        }),
      },
      onError: {
        target: '#idle',
        actions: (context: any, event: any) => { logger.error({ event }, 'removingNoteFromAOFCollection: failed'); },
      },
    },
  },

  selectingAreasOfFocusCollection: {
    id: 'selectingAreasOfFocusCollection',
    entry: [assign<AppStateContext, any>((context, event): any => {
      const { collectionId } = event.data;
      const { areasOfFocusCollections } = context;
      const areaOfFocusOpenNoteObjects: Record<string, any> = {};
      const areaOfFocusOpenNoteIds: string[] = [];

      logger.info({ context, event }, 'selectingAreasOfFocusCollection data');

      const selectedAreaOfFocus = areasOfFocusCollections![collectionId];
      if (!selectedAreaOfFocus) {
        logger.warn({ collectionId }, 'selectingAreasOfFocusCollection: collection not found, skipping');
        return {};
      }

      selectedAreaOfFocus.order_of_items.forEach((noteId: string) => {
        const note = context.notes![noteId];
        if (!note) {
          logger.warn(`Note ${noteId} not found locally during AOF selection.`);
        } else {
          areaOfFocusOpenNoteObjects[noteId] = note;
        }
        areaOfFocusOpenNoteIds.push(noteId);
      });

      const stack = selectedAreaOfFocus?.selected_stack || [];
      let selectedNoteId: string | null = null;

      if (stack.length > 0 && stack[stack.length - 1]) {
        selectedNoteId = stack[stack.length - 1];
      } else if (selectedAreaOfFocus?.order_of_items?.length > 0) {
        const orderOfItems = selectedAreaOfFocus.order_of_items;
        selectedNoteId = orderOfItems[orderOfItems.length - 1];
      }

      const selectedNote = selectedNoteId ? context.notes![selectedNoteId] : null;

      return {
        selectedAreaOfFocus,
        selectedAreaOfFocusId: collectionId,
        areaOfFocusOpenNoteObjects,
        areaOfFocusOpenNoteIds,
        selectedNote,
        selectedNoteId,
        showNoteSearch: false,
      };
    })],
    always: [{ target: '#idle', cond: () => true }],
  },

  creatingAreaOfFocusCollection: {
    id: 'creatingAreaOfFocusCollection',
    invoke: {
      src: createAOFService,
      onDone: {
        target: '#idle',
        actions: assign<AppStateContext, any>((context, event) => {
          const { newCollection } = event.data;
          if (!newCollection) return {};
          const updatedCollections = {
            ...context.areasOfFocusCollections,
            [newCollection.area_of_focus_collection_id]: newCollection,
          };
          return {
            areasOfFocusCollections: updatedCollections,
            areasOfFocusCollectionsIterable: Object.values(updatedCollections),
          };
        }),
      },
      onError: {
        target: '#idle',
        actions: (context: any, event: any) => { logger.error({ error: event.data }, 'creatingAreaOfFocusCollection failed'); },
      },
    },
  },

  renamingAreaOfFocusCollection: {
    id: 'renamingAreaOfFocusCollection',
    invoke: {
      src: renameAOFService,
      onDone: {
        target: '#idle',
        actions: assign<AppStateContext, any>((context, event) => {
          const { collectionId, title, apiResult } = event.data;
          const existing = context.areasOfFocusCollections![collectionId];
          if (!existing) return {};
          const updatedCollection = (apiResult?.area_of_focus_collection_id)
            ? apiResult
            : { ...existing, title, date_modified: new Date().toISOString() };
          const updatedCollections = {
            ...context.areasOfFocusCollections,
            [collectionId]: updatedCollection,
          };
          const isSelected = context.selectedAreaOfFocus?.area_of_focus_collection_id === collectionId;
          return {
            areasOfFocusCollections: updatedCollections,
            areasOfFocusCollectionsIterable: Object.values(updatedCollections),
            ...(isSelected ? { selectedAreaOfFocus: updatedCollection } : {}),
          };
        }),
      },
      onError: {
        target: '#idle',
        actions: (context: any, event: any) => { logger.error({ error: event.data }, 'renamingAreaOfFocusCollection failed'); },
      },
    },
  },

  deletingAreaOfFocusCollection: {
    id: 'deletingAreaOfFocusCollection',
    invoke: {
      src: deleteAOFService,
      onDone: {
        target: '#idle',
        actions: assign<AppStateContext, any>((context, event) => {
          const { collectionId } = event.data;
          const updatedCollections = { ...context.areasOfFocusCollections };
          delete updatedCollections[collectionId];
          const isSelected = context.selectedAreaOfFocus?.area_of_focus_collection_id === collectionId;
          return {
            areasOfFocusCollections: updatedCollections,
            areasOfFocusCollectionsIterable: Object.values(updatedCollections),
            ...(isSelected ? { selectedAreaOfFocus: null, selectedAreaOfFocusId: null, areaOfFocusOpenNoteObjects: {}, areaOfFocusOpenNoteIds: [] } : {}),
          };
        }),
      },
      onError: {
        target: '#idle',
        actions: (context: any, event: any) => { logger.error({ error: event.data }, 'deletingAreaOfFocusCollection failed'); },
      },
    },
  },

  mergingAreaOfFocusCollections: {
    id: 'mergingAreaOfFocusCollections',
    invoke: {
      src: mergeAOFService,
      onDone: {
        target: '#idle',
        actions: assign<AppStateContext, any>((context, event) => {
          const { sourceId, destinationId } = event.data;
          const updatedCollections = { ...context.areasOfFocusCollections };
          delete updatedCollections[sourceId];

          const sourceCollection = context.areasOfFocusCollections![sourceId];
          const destCollection = context.areasOfFocusCollections![destinationId];
          if (destCollection && sourceCollection) {
            const mergedItems = Array.from(new Set([
              ...(destCollection.order_of_items || []),
              ...(sourceCollection.order_of_items || []),
            ]));
            updatedCollections[destinationId] = { ...destCollection, order_of_items: mergedItems };
          }

          const isSelectedSource = context.selectedAreaOfFocus?.area_of_focus_collection_id === sourceId;
          return {
            areasOfFocusCollections: updatedCollections,
            areasOfFocusCollectionsIterable: Object.values(updatedCollections),
            ...(isSelectedSource ? { selectedAreaOfFocus: updatedCollections[destinationId] || null, selectedAreaOfFocusId: destinationId } : {}),
          };
        }),
      },
      onError: {
        target: '#idle',
        actions: (context: any, event: any) => { logger.error({ error: event.data }, 'mergingAreaOfFocusCollections failed'); },
      },
    },
  },
};
